import React, { useState } from "react";
import { Category, expandedSectionTitles } from "../../data/faqData";

interface FAQ {
  question: string;
  answer: string;
}

interface CategoryFaqs {
  [key: string]: FAQ[];
}

interface FAQSectionProps {
  setVisibleSection: (section: "none" | "faq" | "notify") => void;
  showSection: (section: "none" | "faq" | "notify") => void;
  categoryFaqs: CategoryFaqs;
  activeCategory: Category;
  toggleQuestion: (id: string) => void;
  activeQuestionId: string | null;
  categoryDisplayNames: Record<Category, string>;
  getBackgroundColor: (category: Category) => string;
  handleCategoryView: (category: Category) => void;
  setActiveQuestionId: (id: string | null) => void;
  viewedCategories: Set<Category>;
  customClassName?: string;
}

const FAQSection: React.FC<FAQSectionProps> = ({
  setVisibleSection,
  showSection,
  categoryFaqs,
  activeCategory,
  toggleQuestion,
  activeQuestionId,
  categoryDisplayNames,
  getBackgroundColor,
  handleCategoryView,
  viewedCategories,
  customClassName,
}) => {
  const handleClose = () => {
    setVisibleSection("none");
  };

  return (
    <div className="fixed inset-0 bg-white">
      <div className="relative h-full flex">
        {/* Category Sidebar */}
        <div className="absolute left-0 top-0 bottom-0 z-20 flex flex-col">
          {(Object.keys(categoryFaqs) as Category[]).map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryView(category)}
              className={`w-16 h-1/4 cursor-pointer transition-all duration-300
                ${getBackgroundColor(category)} 
                ${
                  category === activeCategory
                    ? "opacity-100"
                    : "opacity-50 hover:opacity-100"
                }`}
            >
              <span
                className="text-black text-lg font-sedan font-semibold whitespace-nowrap transform -rotate-180"
                style={{ writingMode: "vertical-rl" }}
              >
                {categoryDisplayNames[category]}
              </span>
            </button>
          ))}
        </div>

        {/* FAQ Content */}
        <div className="flex-1 relative overflow-hidden pl-16">
          {/* Top Header with Logo and Button */}
          <div className="absolute top-0 left-16 right-0 flex justify-between items-center pt-6 z-10">
            <img
              src="/images/logoonly.png"
              alt="Logo"
              className="w-14 h-14 object-contain cursor-pointer"
              onClick={handleClose}
            />
            <button
              onClick={() => showSection("notify")}
              className="px-6 py-2 mr-16 bg-[#91A93D] text-white rounded-full hover:bg-[#798F33] transition-colors"
            >
              Join Waitlist
            </button>
          </div>

          {/* FAQ Section */}
          <div className="h-full relative">
            {(Object.keys(categoryFaqs) as Category[]).map((category) => {
              const isViewed = viewedCategories.has(category);
              const isActive = category === activeCategory;

              const categoryOrder = Object.keys(categoryFaqs);
              const activeIndex = categoryOrder.indexOf(activeCategory);
              const currentIndex = categoryOrder.indexOf(category);

              const position = 
                isActive
                  ? "translate-x-0"
                  : currentIndex > activeIndex
                  ? "-translate-x-full"
                  : "translate-x-full";

              return (
                <div
                  key={category}
                  className={`absolute inset-0 w-full transition-transform duration-500 ease-in-out
                    ${getBackgroundColor(category)} ${position}`}
                >
                  {isActive && (
                    <div
                      className={`h-full pt-24 px-8 overflow-y-auto ${
                        customClassName || ""
                      }`}
                    >
                      <div className="max-w-3xl mx-auto space-y-8">
                        <div className="text-black">
                          <h2 className="text-3xl text-center font-quicksand font-bold mb-6">
                            {expandedSectionTitles[category]} {/* Use expanded section titles */}
                          </h2>
                        </div>

                        {/* Questions and Answers */}
                        {categoryFaqs[category].map((faq, index) => {
                          const questionId = `${category}-${index}`;
                          const isOpen = activeQuestionId === questionId;

                          return (
                            <div
                              key={questionId}
                              className="border-b border-white/20"
                            >
                              <button
                                onClick={() => toggleQuestion(questionId)}
                                className="w-full py-4 flex justify-between items-center text-left hover:bg-white/5 rounded-t-lg"
                              >
                                <h3 className="text-lg font-semibold text-black pr-8">
                                  {faq.question}
                                </h3>
                                <span className="text-2xl transform transition-transform">
                                  {isOpen ? "−" : "+"}
                                </span>
                              </button>

                              {/* Answer Section */}
                              <div
                                className={`overflow-hidden transition-all duration-300 ${
                                  isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                                }`}
                              >
                                <p className="p-2 text-black whitespace-pre-line">
                                  {faq.answer}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;
